import { Box, Modal, Typography } from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import QRCode from "qrcode";
import ButtonComponent from "components/button/ButtonComponent";
import LabelHeader from "components/Label/LabelHeader";
import LabelValueDisplay from "components/LabelValueDisplay";
import { getUserType } from "util/auth";
import { formatPhoneNumber } from "utils";
import Notify from "config/notify";
import routes from "constants/routes";
import { HEADER_EST_RESULT, HEADER_EST_SUB_RESULT } from "constants/data";
import useQuotationDetail from "features/quotation/hooks/useQuotationDetail";
import useQuotationHeaderData from "features/quotation/hooks/useQuotationHeaderData";
import useQuotationWheelData from "features/quotation/hooks/useQuotationWheelData";
import QuotationDiscountTable from "features/quotation/components/QuotationDiscountTable";
import QuotationDetailTable from "features/quotation/components/QuotationDetailTable";
import PreviewPdfEst from "features/car_model/components/PreviewPdfEst";
import PreviewWheelTirePdfEst from "features/car_model/components/PreviewWheelTirePdfEst";
import ServiceCostSummary from "features/mst_proc_fee/components/ServiceCostSummary";
import WheeItemRow from "features/mst_proc_fee/components/WheelItemRow";
import QuotationResultTableTire from "features/mst_proc_fee/components/QuotationResultTableTire";

const STYLES = {
  header: {
    backgroundColor: "var(--primary-500)",
    textAlign: "center" as const,
    padding: "10px 0",
    fontSize: { xs: 16, md: 18, lg: 20 },
  },
  image: {
    width: { xs: 42, lg: 72 },
    height: { xs: 42, lg: 72 },
  },
  btn: {
    fontSize: { xs: "16px", lg: "17px" },
    height: { xs: "110px", lg: "124px" },
    width: { xs: "110px", lg: "130px" },
    flexDirection: "column" as const,
    border: "none",
    boxShadow: "none",
    whiteSpace: "nowrap" as const,
  },
} as const;

// Memoized header component
const QuoteHeader = memo(() => (
  <Typography
    variant="h2"
    sx={{
      fontSize: { xs: 28, lg: 31 },
      fontWeight: "medium",
      color: "var(--gray-700)",
      mb: 5,
    }}
  >
    見積詳細
  </Typography>
));

QuoteHeader.displayName = "QuoteHeader";

// Memoized info item component for header
interface InfoItemProps {
  label: string;
  value: string;
  width?: any;
}

const InfoItem = memo<InfoItemProps>(({ label, value, width }) => (
  <Box
    sx={{
      ...width,
      display: "flex",
      gap: "3px",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <LabelHeader label={label} sxBox={{ justifyContent: "center" }} />
    <Box
      textAlign="center"
      margin="5px auto"
      py="5px"
      fontSize={{ xs: 16, md: 18, lg: 20 }}
      minHeight="30px"
      whiteSpace="pre-wrap"
    >
      {value}
    </Box>
  </Box>
));

InfoItem.displayName = "InfoItem";

// Memoized action buttons
interface ActionButtonsProps {
  onQrClick: () => void;
  onPdfClick: () => void;
  onSmsClick: () => void;
  isGeneratingPdf: boolean;
}

const ActionButtons = memo<ActionButtonsProps>(
  ({ onQrClick, onPdfClick, onSmsClick, isGeneratingPdf }) => (
    <Box sx={{ display: "flex", gap: "20px", justifyContent: "end", mt: 3 }}>
      <ButtonComponent
        src="/img/icons/qr.svg"
        sxImage={STYLES.image}
        label=""
        sx={STYLES.btn}
        onClick={onQrClick}
      />
      <ButtonComponent
        src="/img/icons/pdf.svg"
        sxImage={STYLES.image}
        label=""
        sx={STYLES.btn}
        onClick={onPdfClick}
        disabled={isGeneratingPdf}
      />
      <ButtonComponent
        src="/img/icons/phone-sms.svg"
        sxImage={STYLES.image}
        label=""
        sx={STYLES.btn}
        onClick={onSmsClick}
        disabled={isGeneratingPdf}
      />
    </Box>
  ),
);

ActionButtons.displayName = "ActionButtons";

/**
 * QuotationDetail component displays the details of a quotation.
 * Supports both tire and wheel type quotations with different rendering.
 *
 * Features:
 * - Shows quotation details, customer info, and vehicle info.
 * - Provides actions: show QR code, open PDF in new tab, send PDF via SMS.
 * - Conditional rendering based on quote_type:
 *   - tire: Displays QuotationDiscountTable and QuotationDetailTable
 *   - wheel: Displays ServiceCostSummary, WheelItemRow, and QuotationResultTableTire
 */
function QuotationDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = getUserType();
  const [showQr, setShowQr] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [selectedId, setSelectedId] = useState<number[]>([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Use custom hooks for data management
  const {
    quotation,
    quotationId,
    isTireType,
    isWheelType,
    firstTireDetail,
    tireSizeRender,
    hubRimDisplay,
    carLabel,
    handleSendSmsWithPdf,
  } = useQuotationDetail();

  const { customerInfo, orderInfo } = useQuotationHeaderData({
    quotation,
    userType,
    tireSizeRender,
    hubRimDisplay,
    carLabel,
    isTireType,
  });

  const { optionsInfo, wheelOptionsPrice, wheelData, roundingValue } = useQuotationWheelData({
    quotation,
    firstTireDetail,
    isWheelType,
  });

  // Sub header for wheel tire table
  const subHeaderResult = useMemo(
    () => [
      ...HEADER_EST_SUB_RESULT.slice(0, -1),
      {
        label: (
          <Typography
            component="span"
            sx={{ fontWeight: "bold", fontSize: { xs: "16px", md: "18px", xl: "20px" } }}
          >
            見積金額 <br />{" "}
            <Typography sx={{ fontWeight: "inherit", fontSize: 10 }}>
              (タイヤホイールセット)
            </Typography>
          </Typography>
        ),
        sx: { backgroundColor: "var(--red-500)" },
      },
    ],
    [],
  );

  // Handlers
  const handleSelect = useCallback((id: number, checked: boolean) => {
    if (checked) {
      setSelectedId((prev) => [...prev, id]);
    } else {
      setSelectedId((prev) => prev.filter((item) => item !== id));
    }
  }, []);

  const onSendSmsWithPdf = async () => {
    if (!quotation) {
      Notify.setNotification({
        title: "見積もりデータがありません",
        show: true,
        actionsPosition: "center",
      });
      return;
    }

    setIsGeneratingPdf(true);
    try {
      // Generate QR code data with wheel parameter for wheel type
      const qrCodeUrl = isWheelType
        ? `${window.location.origin}/quotation-qr/${quotation.quote_id}?qr_read=yes&wheel=yes`
        : `${window.location.origin}/quotation-qr/${quotation.quote_id}?qr_read=yes`;

      const qrCodeData = await QRCode.toDataURL(qrCodeUrl);

      // Create PDF component based on type
      const pdfElement = isWheelType ? (
        <PreviewWheelTirePdfEst quotationDetail={quotation} qrCodeData={qrCodeData} optionsPrice={wheelOptionsPrice} />
      ) : (
        <PreviewPdfEst quotationDetail={quotation} qrCodeData={qrCodeData} />
      );

      // Send SMS with PDF (sns_send_flag: 1 = SMS送信)
      await handleSendSmsWithPdf(pdfElement, 1);

      setShowContact(true);
    } catch (error) {
      console.error("SMS送信エラー:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  /**
   * Upload PDF via SMS API with sns_send_flag=0, then open the returned S3 URL in a new tab.
   * The S3 endpoint serves the PDF with `Content-Type: application/pdf`, so iPad Safari and
   * other browsers open it inline in their native PDF viewer (no client-side blob clipping).
   */
  const openPdfNewTab = useCallback(async () => {
    if (!quotation) return;

    // Open a blank tab synchronously so iOS Safari preserves the user-gesture context.
    // We'll update its location after the async upload completes.
    const newTab = window.open("", "_blank");

    setIsGeneratingPdf(true);
    try {
      const qrCodeUrl = isWheelType
        ? `${window.location.origin}/quotation-qr/${quotation.quote_id}?qr_read=yes&wheel=yes`
        : `${window.location.origin}/quotation-qr/${quotation.quote_id}?qr_read=yes`;
      const qrCodeData = await QRCode.toDataURL(qrCodeUrl);

      const pdfElement = isWheelType ? (
        <PreviewWheelTirePdfEst
          quotationDetail={quotation}
          qrCodeData={qrCodeData}
          optionsPrice={wheelOptionsPrice}
        />
      ) : (
        <PreviewPdfEst quotationDetail={quotation} qrCodeData={qrCodeData} />
      );

      // Upload PDF (sns_send_flag: 0 = PDF出力のみ、SMS送信しない)
      const response = await handleSendSmsWithPdf(pdfElement, 0);

      if (response?.url) {
        if (newTab && !newTab.closed) {
          newTab.location.href = response.url;
        } else {
          // Tab was blocked/closed; fall back to same-window navigation
          window.open(response.url, "_blank");
        }
      } else {
        newTab?.close();
        Notify.setNotification({
          title: "PDFの生成に失敗しました",
          show: true,
          actionsPosition: "center",
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("PDF生成エラー:", error);
      newTab?.close();
      Notify.setNotification({
        title: "PDFの生成に失敗しました",
        show: true,
        actionsPosition: "center",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [quotation, isWheelType, wheelOptionsPrice, handleSendSmsWithPdf]);

  const backToList = useCallback(() => {
    const params = (location.state as any)?.queryParams;

    navigate(
      {
        pathname: routes.quoteList,
      },
      {
        state: { queryParams: params },
      },
    );
  }, [location.state, navigate]);

  if (!quotation) {
    return null;
  }

  return (
    <>
      {/* Quote Header Section - Common for both types */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          padding: { xs: 2, md: 2.5, xl: 5 },
          paddingBottom: { xs: 2, xl: 3 },
          alignItems: "start",
          columnGap: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: { xs: "100%", xl: "calc((100% - 160px) / 2)" },
          }}
        >
          <Box
            sx={{
              width: isTireType ? "280px" : "220px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <QuoteHeader />
            {quotation.car_image_url && quotation.modl_name && (
              <img
                src={quotation.car_image_url}
                alt="tyre-set"
                width="163px"
                style={{ height: "98px" }}
              />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              columnGap: "2px",
              // rowGap: "10px",
              flexWrap: "wrap",
              flex: 1,
            }}
          >
            {customerInfo.map((el) => (
              <InfoItem key={el.id} label={el.label} value={el.value} width={el.width} />
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            width: { xs: "100%", xl: "calc((100% - 160px) / 2)" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: isWheelType
                ? { xs: "flex-end", xl: "space-around" }
                : "space-between",
              columnGap: "2px",
              rowGap: "10px",
              flexWrap: "wrap",
              flex: 1,
            }}
          >
            {orderInfo.map((el) => (
              <LabelValueDisplay
                key={el.id}
                label={el.label}
                value={el.value}
                type="text"
                containerSx={{
                  minWidth: isWheelType ? 162 : undefined,
                  width: isTireType
                    ? { xs: "calc((100% - 2px) / 2)", md: "calc((100% - 6px) / 4)" }
                    : { xs: "calc((100% - 2px) / 2)", md: "calc((100% - 8px) / 4)" },
                }}
              />
            ))}
          </Box>
          <ActionButtons
            onQrClick={() => setShowQr(true)}
            onPdfClick={openPdfNewTab}
            onSmsClick={onSendSmsWithPdf}
            isGeneratingPdf={isGeneratingPdf}
          />
        </Box>
      </Box>

      {/* Quote Details Section - Conditional rendering based on type */}
      <Box
        sx={{
          backgroundColor: "var(--offwhite-100)",
          padding: isTireType
            ? { xs: "20px 16px", md: "20px", xl: "30px 40px" }
            : { xs: 2, md: 2.5, lg: 5 },
        }}
      >
        {/* Tire Type */}
        {isTireType && (
          <>
            <Box
              sx={{
                display: { md: "block", xl: "flex" },
                flexWrap: { md: "wrap", xl: "nowrap" },
                rowGap: "20px",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "end" }}>
                <ButtonComponent label="一覧へ戻る" onClick={backToList} />
              </Box>
              <Box
                sx={{
                  mt: { xs: 2, xl: 0 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box sx={{ overflowX: "auto", width: "100%" }}>
                  <QuotationDiscountTable quotation={quotation} />
                </Box>
              </Box>
            </Box>
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              {quotation.tbl_quote_detail_tire && (
                <QuotationDetailTable
                  quotations={quotation.tbl_quote_detail_tire}
                  onSelect={handleSelect}
                  selectedId={selectedId}
                />
              )}
            </Box>
          </>
        )}

        {/* Wheel Type */}
        {isWheelType && optionsInfo && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  pb: 1,
                  rowGap: 2,
                }}
              >
                <ButtonComponent label="一覧へ戻る" onClick={backToList} />
              </Box>
              <Box
                sx={{
                  overflowX: "auto",
                  width: { xs: "100%", lg: "auto" },
                }}
              >
                <ServiceCostSummary
                  type="detail"
                  costSettingInfo={optionsInfo}
                  sxBox={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "end",
                    minWidth: "1370px",
                  }}
                  disablePunctureAndCashback
                  hideValve
                />
              </Box>
            </Box>
            {wheelData.map((wheelDetail: any, index: number) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`wheel-${index}`}
                sx={{
                  overflowX: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  py: "2px",
                  width: "100%",
                  boxSizing: "border-box",
                  border: "4px solid #BFBFBF",
                  mt: index === 0 ? "6px" : 0,
                }}
              >
                <WheeItemRow wheel={wheelDetail[0]} optionsPrice={wheelOptionsPrice} detailData />
                <QuotationResultTableTire
                  mainHeader={HEADER_EST_RESULT.filter((item) => item.label !== "選択")}
                  subHeader={subHeaderResult}
                  rounding={roundingValue}
                  quoteDetailTire={wheelDetail[1]}
                  updateQuotationTire={() => {}}
                  detailData
                />
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* QR Code and SMS Modal - Common for both types */}
      <Modal
        open={(showQr || showContact) && quotationId !== undefined}
        onClose={() => {
          setShowQr(false);
          setShowContact(false);
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "var(--white)",
            width: "550px",
            height: "545px",
            alignSelf: "center",
            flexDirection: "column",
            padding: "55px",
            borderRadius: "32px",
            boxSizing: "border-box",
          }}
        >
          {showQr && (
            <QRCodeSVG
              size={316}
              value={
                isWheelType
                  ? `${window.location.origin}/quotation-qr/${quotationId}?qr_read=yes&wheel=yes`
                  : `${window.location.origin}/quotation-qr/${quotationId}?qr_read=yes`
              }
            />
          )}
          {showContact && (
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 33, lg: 56 },
                  fontWeight: "medium",
                  color: "var(--gray-700)",
                }}
              >
                SMS送信
              </Typography>
              <Box sx={{ mt: "89px", pl: 2, mb: "69px" }}>
                <Typography
                  sx={{
                    fontSize: { xs: 20, md: 28, lg: 33 },
                    fontWeight: "medium",
                    color: "var(--gray-700)",
                  }}
                >
                  見積もり書を
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 20, md: 28, lg: 33 },
                    fontWeight: "medium",
                    color: "var(--gray-700)",
                  }}
                >
                  {formatPhoneNumber(quotation.customer_tel_no as string, userType)}へ
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 20, md: 28, lg: 33 },
                    fontWeight: "medium",
                    color: "var(--gray-700)",
                  }}
                >
                  SMS送信しました
                </Typography>
              </Box>
            </Box>
          )}
          <ButtonComponent
            sx={{ width: "116px", borderRadius: "10px", marginTop: "auto" }}
            label="閉じる"
            onClick={() => {
              setShowQr(false);
              setShowContact(false);
            }}
          />
        </Box>
      </Modal>
    </>
  );
}

export default memo(QuotationDetail);
